import { getAuthHeaders, handleAPIResponse } from '@/utils/apiUtils';

import {
  Organisation,
  OnboardingData,
  Employee,
  AnalyticsData,
  type DashboardStats,
  type InviteEmployeeData,
} from '@/types/organisation';
import type { RecentNotificationsResponse } from '@/types/notifications';
import type {
  EmployeeActivityReportPayload,
  EmployeeActivityReportSuccessData,
} from '@/types/reports';
import type { BaseApiResponse } from '@/types/api';

const API_BASE_URL = `${process.env.NEXT_PUBLIC_API_URL}`;

export type { InviteEmployeeData };

export interface SignupData {
  employee: {
    name: string;
    email: string;
    designation: string;
  };
  organisation: {
    name: string;
    website: string;
  };
}

export const organisationApi = {
  signup: async (data: SignupData) => {
    const response = await fetch(`${API_BASE_URL}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    return handleAPIResponse<{ organisation: Organisation }>(response);
  },

  completeOnboarding: async (data: OnboardingData) => {
    const response = await fetch(`${API_BASE_URL}/organisations/onboarding`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return handleAPIResponse<{ organisation: Organisation }>(response);
  },

  getDetails: async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/organisations/${id}`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse<{ organisation: Organisation }>(response);
  },

  update: async (data: Partial<Organisation>, logo?: File) => {
    const formData = new FormData();

    formData.append('name', data.name || '');
    formData.append('website', data.website || '');
    formData.append('description', data.description || '');

    if (logo) {
      formData.append('logo', logo);
    }

    const headers = getAuthHeaders();
    delete headers['Content-Type']; // Let browser set correct content type for FormData

    const response = await fetch(`${API_BASE_URL}/organisations/self`, {
      method: 'PUT',
      headers,
      body: formData,
    });

    return handleAPIResponse<{ organisation: Organisation }>(response);
  },

  getOrganisation: async () => {
    const response = await fetch(`${API_BASE_URL}/organisations/self`, {
      headers: getAuthHeaders(),
      cache: 'no-store',
    });

    return handleAPIResponse<{ organisation: Organisation }>(response);
  },

  getEmployees: async () => {
    const response = await fetch(`${API_BASE_URL}/organisations/employees`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse<{ employees: Employee[] }>(response);
  },

  inviteEmployee: async (data: InviteEmployeeData) => {
    const response = await fetch(`${API_BASE_URL}/organisations/employees/invite`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return handleAPIResponse<{ success: boolean }>(response);
  },

  removeEmployee: async (employeeId: string) => {
    const response = await fetch(`${API_BASE_URL}/organisations/employees/${employeeId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    return handleAPIResponse<{ success: boolean }>(response);
  },

  getDashboardStats: async (): Promise<{ stats: DashboardStats }> => {
    const response = await fetch(`${API_BASE_URL}/organisations/dashboard-stats`, {
      headers: getAuthHeaders(),
    });
    return handleAPIResponse<{ stats: DashboardStats }>(response);
  },

  getAnalytics: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await fetch(`${API_BASE_URL}/organisations/analytics?${params.toString()}`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse<AnalyticsData>(response);
  },

  getRecentNotifications: async (): Promise<RecentNotificationsResponse> => {
    const response = await fetch(`${API_BASE_URL}/organisations/recent-notifications`, {
      headers: getAuthHeaders(),
    });

    return handleAPIResponse<RecentNotificationsResponse>(response);
  },

  async generateEmployeeActivityReport(
    payload?: EmployeeActivityReportPayload
  ): Promise<EmployeeActivityReportSuccessData> {
    const headers = getAuthHeaders(); // Assumes this sets 'Authorization' and base 'Content-Type'

    const rawResponse = await fetch(`${API_BASE_URL}/organisations/employee-activity-report`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json', // Request body is JSON
        // 'Accept': 'text/csv, application/json', // Optional: Client can hint preference
      },
      body: payload ? JSON.stringify(payload) : undefined,
    });

    if (rawResponse.ok) {
      const contentType = rawResponse.headers.get('Content-Type');
      if (contentType && contentType.includes('text/csv')) {
        const disposition = rawResponse.headers.get('Content-Disposition');
        let filename = 'employee_activity_report.csv'; // Default filename
        if (disposition) {
          const filenameMatch = disposition.match(/filename\*?=['"]?([^'"\s]+)['"]?/i);
          if (filenameMatch && filenameMatch[1]) {
            // Decode URI component if filename* is used (RFC 5987)
            try {
              filename = decodeURIComponent(filenameMatch[1]);
            } catch (e) {
              filename = filenameMatch[1]; // Fallback to raw value
            }
          } else {
            // Fallback for simple filename="..."
            const simpleFilenameMatch = disposition.match(/filename=['"]?([^'"\s]+)['"]?/i);
            if (simpleFilenameMatch && simpleFilenameMatch[1]) {
              filename = simpleFilenameMatch[1];
            }
          }
        }
        const csvData = await rawResponse.text();
        return { csvData, filename };
      } else {
        // Successful response, but not CSV. Try to parse as JSON error or throw.
        try {
          const errorData: BaseApiResponse<null> = await rawResponse.json();
          throw new Error(
            errorData.message || 'Report generation succeeded but returned an unexpected format.'
          );
        } catch (e: any) {
          throw new Error(
            e.message || 'Report generation succeeded but returned an unknown format.'
          );
        }
      }
    } else {
      // Handle non-OK responses (e.g., 400, 401, 500). Expected to be JSON.
      try {
        const errorData: BaseApiResponse<null> = await rawResponse.json();
        throw new Error(
          errorData.message || `Failed to generate report (status ${rawResponse.status})`
        );
      } catch (jsonError) {
        const errorText = await rawResponse.text().catch(() => 'Could not retrieve error details.');
        throw new Error(
          `Failed to generate report (status ${rawResponse.status}): ${errorText || rawResponse.statusText}`
        );
      }
    }
  },
};
